<template>
  <div class="app-container">
    <PageHeader title="采购计划明细"  icon="el-icon-shopping-cart-1" />
    <div class="plan-info">
      <el-descriptions title="计划信息" :column="3" border>
        <el-descriptions-item label="计划编号">{{ plan.planNo }}</el-descriptions-item>
        <el-descriptions-item label="计划名称">{{ plan.planName }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusType(plan.status)">{{ getStatusText(plan.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="申请时间">{{ plan.applyTime }}</el-descriptions-item>
        <el-descriptions-item label="总金额">{{ plan.money }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ plan.remark }}</el-descriptions-item>
      </el-descriptions>
    </div>

    <div class="detail-list">
      <div class="operation-bar">
        <el-button type="primary" @click="handleAddDetail">新增采购计划明细</el-button>
        <el-button 
          type="success" 
          v-if="plan.status === 1"
          @click="handleSubmit"
        >提交审核</el-button>
        <el-button icon="el-icon-back" @click="handleBack">返回</el-button>
      </div>

      <el-table
        v-loading="listLoading"
        :data="detailList"
        border
        style="width: 100%"
      >
        <el-table-column prop="goodName" label="商品名称" min-width="120" align="center" />
        <!-- <el-table-column prop="goodModel" label="型号" width="120" align="center" /> -->
        <!-- <el-table-column prop="specs" label="规格" width="120" align="center" /> -->
        <el-table-column label="单价" width="120" align="center">
          <template slot-scope="{row}">
            <span>¥{{ row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="number" label="数量" width="100" align="center" />
        <el-table-column label="金额" width="120" align="center">
          <template slot-scope="{row}">
            <span>¥{{ row.money }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="supplierName" label="供应商" min-width="120" align="center" />
        <el-table-column prop="remark" label="备注" min-width="150" align="center" />
        <el-table-column prop="createdAt" label="创建时间" width="160" align="center">
          <template slot-scope="{row}">
            <span>{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              @click="handleEditDetail(scope.row)"
              style="margin-right: 5px"
            >修改</el-button>
            <el-button
              size="mini"
              type="danger"
              @click="handleDeleteDetail(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 添加商品对话框 -->
    <el-dialog :title="dialogStatus === 'create' ? '新增采购计划明细' : '修改采购计划明细'" :visible.sync="dialogVisible" width="600px">
      <el-form ref="detailForm" :model="detailForm" :rules="detailRules" label-width="100px">
        <el-form-item label="商品名称" prop="goodsId">
          <el-select
            v-model="detailForm.goodsId"
            placeholder="请输入商品名称搜索"
            filterable
            remote
            reserve-keyword
            :remote-method="remoteSearchGoods"
            :loading="goodsLoading"
            style="width: 100%"
            @change="handleGoodsChange"
          >
            <el-option
              v-for="item in goodsOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="商品单价" prop="goodsPrice">
          <el-input-number 
            v-model="detailForm.goodsPrice" 
            :precision="2" 
            :step="0.1"
            :min="0"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="商品数量" prop="goodsQuantity">
          <el-input-number 
            v-model="detailForm.goodsQuantity" 
            :min="1" 
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="供应商" prop="supplierId">
          <el-select
            v-model="detailForm.supplierId"
            placeholder="请输入供应商名称搜索"
            filterable
            remote
            reserve-keyword
            :remote-method="remoteSearchSupplier"
            :loading="supplierLoading"
            style="width: 100%"
          >
            <el-option
              v-for="item in supplierOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="goodsRemark">
          <el-input
            v-model="detailForm.goodsRemark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitDetail">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { addPlanDetail, deletePlanDetail, submitPlanForApproval } from '@/api/purchase'
import request from '@/utils/request'
import PageHeader from '@/components/common/PageHeader.vue'

export default {
  name: 'PurchasePlanDetail',
  components: { PageHeader },
  data() {
    return {
      plan: {},
      detailList: [],
      listLoading: false,
      dialogVisible: false,
      goodsLoading: false,
      supplierLoading: false,
      dialogStatus: 'create',
      detailForm: {
        goodsId: '',
        goodsPrice: 0,
        goodsQuantity: 1,
        goodsRemark: '',
        supplierId: ''
      },
      detailRules: {
        goodsId: [{ required: true, message: '请选择商品', trigger: 'change' }],
        goodsPrice: [{ required: true, message: '请输入商品单价', trigger: 'blur' }],
        goodsQuantity: [{ required: true, message: '请输入商品数量', trigger: 'blur' }],
        supplierId: [{ required: true, message: '请选择供应商', trigger: 'change' }]
      },
      supplierOptions: [],
      goodsOptions: []
    }
  },
  created() {
    this.getPlanDetail()
  },
  methods: {
    handleBack() {
      if (this.$route.query.from === 'myplan') {
        this.$router.push('/purchase/myplan')
      } else {
        this.$router.push('/purchase/plan')
      }
    },
    async getPlanDetail() {
      try {
        const planId = this.$route.params.id
        const response = await request({
          url: '/api/v1/purchasing-plan/detail/list',
          method: 'POST',
          data: {
            planId: planId
          }
        })
        
        console.log('获取到的明细数据:', response.data)
        if (response.status === 1) {
          this.detailList = response.data.map(item => ({
            detailId: item.detailId,
            goodId: item.goodId,
            goodName: item.goodName,
            goodModel: item.goodModel,
            specs: item.specs,
            money: item.money,
            number: item.number,
            planId: item.planId,
            planName: item.planName,
            planNo: item.planNo,
            price: item.price,
            remark: item.remark,
            supplierId: item.supplierId,
            supplierName: item.SupplierName || item.supplierName,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
          }))
          console.log('处理后的明细数据:', this.detailList)
        } else {
          this.$message.error('获取明细数据失败')
        }
      } catch (error) {
        console.error('获取明细数据失败:', error)
        this.$message.error('获取明细数据失败')
      }
    },
    handleAddDetail() {
      this.dialogStatus = 'create'
      this.dialogVisible = true
      this.detailForm = {
        detailId: '',
        goodsId: '',
        goodsPrice: 0,
        goodsQuantity: 1,
        goodsRemark: '',
        supplierId: ''
      }
      this.goodsOptions = []
      this.supplierOptions = []
    },
    submitDetail() {
      this.$refs.detailForm.validate(valid => {
        if (valid) {
          const data = {
            detailId: this.detailForm.detailId,
            planId: this.$route.params.id,
            goodsId: this.detailForm.goodsId,
            goodsPrice: this.detailForm.goodsPrice,
            goodsQuantity: this.detailForm.goodsQuantity,
            goodsRemark: this.detailForm.goodsRemark,
            supplierId: this.detailForm.supplierId
          }
          request({
            url: '/api/v1/purchasing-plan/detail/save',
            method: 'POST',
            data: data
          }).then(response => {
            if (response.status === 1) {
              this.dialogVisible = false
              this.$message({
                type: 'success',
                message: this.dialogStatus === 'create' ? '添加成功' : '修改成功'
              })
              this.getPlanDetail()
            } else {
              this.$message.error(response.msg || (this.dialogStatus === 'create' ? '添加失败' : '修改失败'))
            }
          })
        }
      })
    },
    handleSubmit() {
      this.$confirm('确认提交审核?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        submitPlanForApproval(this.$route.params.id).then(() => {
          this.$message({
            type: 'success',
            message: '提交成功'
          })
          this.getPlanDetail()
        })
      })
    },
    handleDeleteDetail(row) {
      this.$confirm('确认删除该采购明细?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await request({
            url: '/api/v1/purchasing-plan/detail/delete',
            method: 'POST',
            data: {
              detailId: row.detailId
            }
          })
          
          if (response.status === 1) {
            this.$message({
              type: 'success',
              message: '删除成功'
            })
            this.getPlanDetail()
          } else {
            this.$message.error('只有草稿状态和审核不通过状态的采购计划可以删除明细')
            return
          }
        } catch (error) {
          this.$message.error('只有草稿状态和审核不通过状态的采购计划可以删除明细')
        }
      })
    },
    getStatusType(status) {
      const statusMap = {
        1: 'info',
        2: 'warning',
        3: 'success',
        4: 'danger',
        5: 'success',
        6: 'info'
      }
      return statusMap[status]
    },
    getStatusText(status) {
      const statusMap = {
        1: '草稿',
        2: '待审核',
        3: '已审核',
        4: '已驳回',
        5: '已完成',
        6: '已取消'
      }
      return statusMap[status]
    },
    formatDate(date) {
      return new Date(date).toLocaleString()
    },
    async remoteSearchGoods(query) {
      if (query !== '') {
        this.goodsLoading = true
        try {
          const response = await request({
            url: '/api/v1/goods/list',
            method: 'POST',
            data: {
              name: query,
              page: 1,
              size: 20
            }
          })
          
          console.log(response.data)
          if (response.status === 1 && response.data) {
            this.goodsOptions = response.data.map(item => ({
              value: item.GoodID,
              label: item.Name,
              price: item.Price
            }))
          }
          console.log(this.goodsOptions)
        } catch (error) {
          console.error('搜索商品失败:', error)
          this.$message.error('搜索商品失败')
        } finally {
          this.goodsLoading = false
        }
      } else {
        this.goodsOptions = []
      }
    },
    handleGoodsChange(value) {
      const selectedGoods = this.goodsOptions.find(item => item.value === value)
      if (selectedGoods) {
        console.log(selectedGoods)
        this.detailForm.goodsPrice = selectedGoods.price
      }
    },
    async remoteSearchSupplier(query) {
      if (query !== '') {
        this.supplierLoading = true
        try {
          const response = await request({
            url: '/api/v1/supplier/list',
            method: 'POST',
            data: {
              name: "",
              page: 1,
              size: 20,
              type: 1 // 假设1代表供应商类型
            }
          })
          
          console.log('供应商数据:', response.data)
          if (response.status === 1 && response.list) {
            this.supplierOptions = response.list.map(item => ({
              value: item.SupplierID, // 供应商ID作为值
              label: item.SupplierName, // 供应商名称作为显示标签
              contact: item.Contact, // 可以保存其他供应商信息，方便后续使用
              phone: item.Phone
            }))
          }
          console.log('处理后的供应商选项:', this.supplierOptions)
        } catch (error) {
          console.error('搜索供应商失败:', error)
          this.$message.error('搜索供应商失败')
        } finally {
          this.supplierLoading = false
        }
      } else {
        this.supplierOptions = []
      }
    },
    handleEditDetail(row) {
      // 检查计划状态是否为草稿(1)或已拒绝(5)
      if (this.plan.status !== 1 && this.plan.status !== 5) {
// -       this.$message.error('当前状态不允许修改')
+       this.$message.error('只有草稿状态和审核不通过状态的采购计划可以修改明细')
        return
      }

  this.dialogStatus = 'edit'
      this.dialogVisible = true
      this.detailForm = {
        detailId: row.detailId,
        goodsId: row.goodId,
        goodsPrice: row.price,
        goodsQuantity: row.number,
        goodsRemark: row.remark,
        supplierId: row.supplierId
      }
      this.goodsOptions = [{
        value: row.goodId,
        label: row.goodName,
        price: row.price
      }]
      this.supplierOptions = [{
        value: row.supplierId,
        label: row.supplierName
      }]
    }
  }
}
</script>

<style lang="scss" scoped>
.plan-info {
  margin-bottom: 20px;
}
.operation-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}


.dealer-manage {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 84px);

  .search-card,
  .table-card {
    background: #fff;
    border-radius: 4px;
    margin-bottom: 20px;
    box-shadow: 0 1px 4px rgba(0, 21, 41, .08);
    padding: 0;
  }

  .filter-container {
    padding-bottom: 10px;
  }

  .operation-container {
    margin-bottom: 20px;
  }

  .pagination-container {
    padding: 20px 0;
    text-align: right;
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .upload-demo {
    margin-bottom: 20px;
  }

  .el-upload__tip {
    line-height: 1.2;
    margin-top: 5px;
    color: #909399;
  }
}



</style> 